/**
 * "Your collections" — for Developers and Administrators (ADR-0070): the
 * collections they own or were given, newest change first, with their size,
 * last build and status. Collections are a building surface — a User cannot
 * open a collection's page — so the card is not shown to a User, nor to anyone
 * who has none.
 */

import { Anchor, Badge, Divider, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { collectionStatusColor } from "@/features/collections/collectionMeta";
import { listIndexes, type IndexOut } from "@/shared/api/indexes";

const SHOWN = 5;

const BUILT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

function line(c: IndexOut): string {
  const docs = c.document_count === 1 ? "1 document" : `${c.document_count} documents`;
  if (c.status === "building") return `${docs} · building…`;
  return c.last_built_at_utc
    ? `${docs} · built ${BUILT.format(new Date(c.last_built_at_utc))}`
    : docs;
}

export function CollectionsCard() {
  const getToken = useAccessToken();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [items, setItems] = useState<IndexOut[] | null>(null);

  // Read once when Home opens; the token getter through a ref (an unstable
  // dependency here would reload forever).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const out = await listIndexes(await getTokenRef.current());
        if (!cancelled) setItems(out.items);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items || items.length === 0) return null;
  const shown = [...items]
    .sort((a, b) => (b.updated_at_utc ?? "").localeCompare(a.updated_at_utc ?? ""))
    .slice(0, SHOWN);

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="xs">
        <Title order={4}>Your collections</Title>
        {items.length > SHOWN && (
          <Anchor component={RouterLink} to="/development/collections" size="sm">
            All {items.length}
          </Anchor>
        )}
      </Group>
      <Stack gap={0}>
        {shown.map((c, i) => (
          <Fragment key={c.index_id}>
            {i > 0 && <Divider />}
            <Group justify="space-between" wrap="nowrap" py="sm" gap="sm">
              <div style={{ minWidth: 0 }}>
                <Anchor
                  component={RouterLink}
                  to={`/development/collections/${encodeURIComponent(c.index_id)}`}
                  size="sm"
                  fw={500}
                  c="var(--mantine-color-text)"
                  style={{ display: "block" }}
                  truncate
                >
                  {c.name}
                </Anchor>
                <Text size="xs" c="dimmed">
                  {line(c)}
                </Text>
              </div>
              <Badge variant="light" color={collectionStatusColor(c.status)} tt="capitalize">
                {c.status}
              </Badge>
            </Group>
          </Fragment>
        ))}
      </Stack>
    </Paper>
  );
}
